import StateObserver, { now } from 'src/StateObserver';
import { IAPAnalytics } from './analytics.types';
import { serverSyncedLocalTime } from 'src/lib/utils';
import { GCInstant } from 'src/lib/gcinstant';
import { TodayCounter } from 'src/replicant/analytics/analytics.schema';
import { getDatestamp } from 'src/replicant/analytics/analytics.getters';
import { arePaymentsAvailable } from 'src/lib/iap/iap.util';
import { hasValidSaveState } from 'src/replicant/match2/savestate/match2-savestate.getters';
import { isFirstEntry } from 'src/lib/platformUtils';
import {
  incrementCancelled,
  resetCancelledExact,
  resetCancelledSession,
  updateSessionPurchaseAnalytics,
} from 'src/state/analytics';
import { track } from 'src/lib/analytics/utils';
import type {
  FeatureSequenceProperties,
  GenericPurchaseAnalyticsProperties,
  PurchaseEndFlowProperties,
} from './iap.purchase.schema';

// ============================================================================
// Constants & Types
// ============================================================================

const SCHEMA_VERSION = '2023-07-24';

const PURCHASE_EVENTS = {
  start: 'PurchaseFlowStart',
  tapped: 'PurchaseTapped',
  failure: 'PurchaseFailure',
  success: 'PurchaseSuccess',
  end: 'PurchaseFlowEnd',
} as const;

export type PurchaseAction = keyof typeof PURCHASE_EVENTS;

type PurchaseAnalytics = IAPAnalytics & { elapsedSeconds: number };

type PurchaseFailureAnalytics = PurchaseAnalytics & {
  userCancelled: boolean;
  errorMessage: string;
  errorCode: string;
};

type GemsAnalytics = {
  beforeFreeGems: number;
  beforePaidGems: number;
  afterFreeGems: number;
  afterPaidGems: number;
};

type PurchaseSuccessAnalytics = PurchaseAnalytics &
  GemsAnalytics & {
    isFirst: boolean;
    sequenceCancelledExact: number;
    sequenceCancelledFeature: number;
    sequenceCancelledScreen: number;
    sequenceCancelledSession: number;
    sequenceCancelledToday: number;
  };

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Gets today's counter value based on purchase time
 */
function getTodayCounter(
  purchaseTime: number,
  counter: TodayCounter | null,
): number {
  if (!counter) return 0;

  const datestamp = getDatestamp(purchaseTime);
  return datestamp > counter.datestamp ? 0 : counter.count;
}

/**
 * Determines revenue type based on platform
 */
function getRevenueType(): string {
  switch (GCInstant.osType) {
    case 'ANDROID_APP':
      return 'androidIAP';
    case 'IOS_APP':
      return 'iosIAP';
    default:
      return 'linePayments';
  }
}

/**
 * Calculates elapsed seconds since the purchase was shown
 */
function getElapsedSeconds(): number {
  return (
    (Date.now() - StateObserver.getState().analytics.purchaseShownTime) / 1000
  );
}

// ============================================================================
// Type Guards
// ============================================================================

function isPurchaseFailureAnalytics(
  opts: PurchaseAnalytics | PurchaseFailureAnalytics | PurchaseSuccessAnalytics,
): opts is PurchaseFailureAnalytics {
  return 'errorMessage' in opts && 'errorCode' in opts;
}

function isPurchaseHasGemsAnalytics(
  opts: PurchaseAnalytics | PurchaseFailureAnalytics | PurchaseSuccessAnalytics,
): opts is PurchaseSuccessAnalytics {
  return 'beforeFreeGems' in opts;
}

// ============================================================================
// Core Analytics Tracking
// ============================================================================

/**
 * Updates the purchase analytics with the provided purchase action and options.
 *
 * @param purchaseAction - The action related to the purchase (e.g., purchase initiated, purchase completed).
 * @param opts - The analytics options which can be of type `PurchaseAnalytics`, `PurchaseFailureAnalytics`, or `PurchaseSuccessAnalytics`.
 *
 * The function retrieves the current state, extracts the purchase time from the analytics state,
 * and constructs an update options object. It then updates the purchase analytics in the state
 * observer and dispatches the updated session purchase analytics.
 */
function updatePurchaseAnalytics(
  purchaseAction: PurchaseAction,
  opts: PurchaseAnalytics | PurchaseFailureAnalytics | PurchaseSuccessAnalytics,
) {
  const state = StateObserver.getState();
  const purchaseTime = state.analytics.purchaseTime;

  const updateOpts = {
    purchaseAction,
    purchaseTime,
    feature: opts.feature,
  };

  // Update stats
  StateObserver.invoke.updatePurchaseAnalytics(updateOpts);
  StateObserver.dispatch(updateSessionPurchaseAnalytics(updateOpts));
}

/**
 * Base purchase analytics tracking function
 */
function getGenericProperties(
  purchaseAction: PurchaseAction,
  opts: PurchaseAnalytics | PurchaseFailureAnalytics | PurchaseSuccessAnalytics,
): GenericPurchaseAnalyticsProperties {
  const state = StateObserver.getState();
  const purchaseTime = state.analytics.purchaseTime;
  const { purchases } = state.user.analytics;
  const sessionPurchases = state.analytics.purchases;
  const persistentCounter = purchases[purchaseAction];
  const sessionCounter = sessionPurchases[purchaseAction];

  const genericProperties: GenericPurchaseAnalyticsProperties = {
    // Specif data
    elapsedSeconds: opts.elapsedSeconds,

    // This we get outside
    feature: opts.feature,
    subFeature: opts.subFeature,
    purchaseCountBefore: opts.purchaseCountBefore,
    productID: opts.productID,
    dollarToLocalRate: opts.dollarToLocalRate,
    revenueGrossLocal: opts.revenueGrossLocal,
    revenueGross: opts.revenueGross,
    revenueNet: opts.revenueNet,
    currency: opts.currency,
    purchaseProvider: opts.purchaseProvider,
    purchaseProviderMethod: opts.purchaseProviderMethod,

    // This analytics we generate here
    revenueType: getRevenueType(),
    breadcrumb: state.ui.stack,
    firstEntrySequenceSchema: purchases.schemaVersion || undefined,
    sequenceAbsolute: persistentCounter.absolute,
    sequenceSession: sessionCounter.sequence,
    sequenceToday: getTodayCounter(purchaseTime, persistentCounter.today),
    arePaymentsAvailable: arePaymentsAvailable(state),
    isCatalogLoaded: GCInstant.catalog.length > 0,
    saveStateAvailable: hasValidSaveState(state.user, now()),

    // New filed
    purchaseID: opts.purchaseID,
  };

  if ('purchaseProviderCreditCardBrand' in opts) {
    genericProperties.purchaseProviderCreditCardBrand =
      opts.purchaseProviderCreditCardBrand;
  }

  return genericProperties;
}

/**
 * Retrieves the feature sequence properties for a given purchase action.
 *
 * @param purchaseAction - The action associated with the purchase.
 * @param opts - The analytics options which can be of type PurchaseAnalytics, PurchaseFailureAnalytics, or PurchaseSuccessAnalytics.
 * @returns The feature sequence properties including absolute, session, and today's sequence counts.
 */
function getFeatureSequenceProperties(
  purchaseAction: PurchaseAction,
  opts: PurchaseAnalytics | PurchaseFailureAnalytics | PurchaseSuccessAnalytics,
): FeatureSequenceProperties {
  const state = StateObserver.getState();
  const purchaseTime = state.analytics.purchaseTime;
  const { purchases } = state.user.analytics;
  const sessionPurchases = state.analytics.purchases;
  const persistentCounter = purchases[purchaseAction];
  const sessionCounter = sessionPurchases[purchaseAction];

  const properties: FeatureSequenceProperties = {
    sequenceFeatureAbsolute:
      persistentCounter.absoluteByFeature[opts.feature] ?? 0,
    sequenceFeatureSession: sessionCounter.sequenceByFeature[opts.feature] ?? 0,
    sequenceFeatureToday: getTodayCounter(
      purchaseTime,
      persistentCounter.todayByFeature[opts.feature],
    ),
  };

  return properties;
}

// ============================================================================
// Flow start and finish tracking
// ============================================================================

/**
 * Tracks the start of a purchase flow.
 *
 * This function records the elapsed time since the start of the purchase flow,
 * updates purchase analytics, and sends a 'PurchaseFlowStart' event with the
 * relevant properties.
 *
 * @param opts - The analytics options for the in-app purchase.
 * @param opts.userId - The ID of the user making the purchase.
 * @param opts.productId - The ID of the product being purchased.
 * @param opts.price - The price of the product being purchased.
 * @param opts.currency - The currency of the price.
 * @param opts.platform - The platform on which the purchase is being made.
 */
export function trackPurchaseFlowStart(opts: IAPAnalytics): void {
  const elapsedSeconds = getElapsedSeconds();
  const props = { ...opts, elapsedSeconds };

  // Update purchase stats
  // Should be called before getGenericProperties
  updatePurchaseAnalytics('start', props);

  // Send purchase flow end event
  track('PurchaseFlowStart', {
    ...getGenericProperties('start', props),
    ...getFeatureSequenceProperties('start', props),
  });
}

/**
 * Tracks the end of a purchase flow by sending relevant analytics data.
 *
 * @param opts - An object containing various analytics data related to the purchase.
 *
 * The `opts` parameter can include:
 * - `PurchaseAnalytics`: Basic purchase analytics data.
 * - `Partial<GemsAnalytics>`: Optional gems-related analytics data.
 * - `Partial<PurchaseFailureAnalytics>`: Optional failure-related analytics data.
 *
 * The function performs the following steps:
 * 1. Calculates the elapsed time since the purchase flow started.
 * 2. Updates purchase analytics with the 'end' event.
 * 3. Gathers common properties for the purchase end flow event.
 * 4. Adds user cancellation information if available.
 * 5. Adds error information if the purchase failed.
 * 6. Adds gems information if the purchase was successful.
 * 7. Sends the 'PurchaseFlowEnd' event with the collected properties.
 */
export function trackPurchaseFlowEnd(
  opts: PurchaseAnalytics &
    Partial<GemsAnalytics> &
    Partial<PurchaseFailureAnalytics>,
): void {
  const elapsedSeconds = getElapsedSeconds();
  const props = { ...opts, elapsedSeconds };

  // Update purchase stats
  // Should be called before getGenericProperties
  updatePurchaseAnalytics('end', props);

  // Common properties
  const properties: PurchaseEndFlowProperties &
    GenericPurchaseAnalyticsProperties &
    FeatureSequenceProperties = {
    ...getGenericProperties('end', props),
    ...getFeatureSequenceProperties('end', props),
  };

  if ('userCancelled' in opts) {
    properties.userCancelled = opts.userCancelled;
  }

  // May include error analytics if purchase was failed
  if (isPurchaseFailureAnalytics(opts)) {
    properties.errorMessage = opts.errorMessage;
    properties.errorCode = opts.errorCode;
  }

  // May include gems analytics if purchase was successful
  if (isPurchaseHasGemsAnalytics(opts)) {
    properties.beforeFreeGems = opts.beforeFreeGems;
    properties.beforePaidGems = opts.beforePaidGems;
    properties.afterFreeGems = opts.afterFreeGems;
    properties.afterPaidGems = opts.afterPaidGems;
  }

  // Send purchase flow end event
  track('PurchaseFlowEnd', properties);
}

// ============================================================================
// Exported Functions
// ============================================================================

/**
 * Initializes purchase analytics on first entry
 */
export function onEntryPurchaseAnalytics(): void {
  if (isFirstEntry()) {
    StateObserver.invoke.setSchemaVersion({ version: SCHEMA_VERSION });
  }
}

/**
 * Tracks the event when a purchase is tapped by the user.
 *
 * This function performs the following actions:
 * 1. Calculates the elapsed time since a reference point.
 * 2. Updates the purchase analytics with the 'tapped' event.
 * 3. Initiates the purchase flow tracking.
 * 4. Sends the 'PurchaseTapped' event with relevant properties.
 *
 * @param opts - The analytics options for the in-app purchase event.
 */
export function trackPurchaseTapped(opts: IAPAnalytics): void {
  const elapsedSeconds = getElapsedSeconds();
  const props = { ...opts, elapsedSeconds };

  // Send purchase flow start together with purchase tapped
  trackPurchaseFlowStart(opts);

  // Update purchase stats
  // Should be called before getGenericProperties
  updatePurchaseAnalytics('tapped', props);

  // Send purchase tapped event
  track('PurchaseTapped', {
    ...getGenericProperties('tapped', props),
    ...getFeatureSequenceProperties('tapped', props),
  });
}

/**
 * Tracks a purchase failure event and updates relevant analytics.
 *
 * @param {PurchaseFailureAnalytics} opts - The options for the purchase failure analytics.
 * @param {string} opts.feature - The feature associated with the purchase.
 * @param {string} opts.productID - The ID of the product being purchased.
 * @param {boolean} opts.userCancelled - Indicates if the user cancelled the purchase.
 * @param {string} opts.errorMessage - The error message associated with the purchase failure.
 * @param {number} opts.errorCode - The error code associated with the purchase failure.
 *
 * This function performs the following actions:
 * - Updates purchase analytics with a 'failure' status.
 * - Sends a 'PurchaseFailure' event with generic and feature-specific properties.
 * - Sends a purchase flow end event.
 * - If the user cancelled the purchase, updates cancelled analytics stats and dispatches an incrementCancelled action.
 */
export function trackPurchaseFailure(opts: PurchaseFailureAnalytics): void {
  // Update purchase stats
  // Should be called before getGenericProperties
  updatePurchaseAnalytics('failure', opts);

  // Send purchase failure event
  track('PurchaseFailure', {
    ...getGenericProperties('failure', opts),
    ...getFeatureSequenceProperties('failure', opts),
    userCancelled: opts.userCancelled,
    errorMessage: opts.errorMessage,
    errorCode: opts.errorCode,
  });

  // Send purchase flow end event
  trackPurchaseFlowEnd(opts);

  // Update cancelled analytics stats
  if (opts.userCancelled) {
    const state = StateObserver.getState();
    const screenKey = state.ui.currentPopup;
    const exactKey = opts.productID + screenKey;
    const timeNow = serverSyncedLocalTime();

    StateObserver.invoke.updateCancelledAnalytics({
      featureKey: opts.feature,
      timeNow,
    });

    StateObserver.dispatch(incrementCancelled({ screenKey, exactKey }));
  }
}

/**
 * Tracks the successful purchase event and updates the relevant analytics.
 *
 * @param opts - The purchase analytics options which include details about the purchase and gems analytics.
 *
 * This function performs the following actions:
 * 1. Updates the purchase statistics by calling `updatePurchaseAnalytics` with the status 'success'.
 * 2. Sends a 'PurchaseSuccess' event with various properties including:
 *    - Generic properties obtained from `getGenericProperties`.
 *    - Feature sequence properties obtained from `getFeatureSequenceProperties`.
 *    - Whether this is the first purchase (`isFirst`).
 *    - Various cancelled analytics counters (exact, feature, screen, session, today).
 *    - Gems analytics (before and after free/paid gems).
 * 3. Sends a purchase flow end event by calling `trackPurchaseFlowEnd`.
 * 4. Resets the cancelled analytics by invoking `StateObserver.invoke.resetCancelledAnalytics` and dispatching `resetCancelledExact` and `resetCancelledSession`.
 */
export function trackPurchaseSuccess(
  opts: PurchaseAnalytics & GemsAnalytics,
): void {
  const state = StateObserver.getState();
  const { purchases } = state.user.analytics;
  const sessionAnalytics = state.analytics;
  const timeNow = serverSyncedLocalTime();
  const screenKey = state.ui.currentPopup;
  const exactKey = opts.productID + screenKey;

  // Update purchase stats
  // Should be called before getGenericProperties
  updatePurchaseAnalytics('success', opts);

  // Send purchase success event
  track('PurchaseSuccess', {
    ...getGenericProperties('success', opts),
    ...getFeatureSequenceProperties('success', opts),
    // Is first purchase
    isFirst: opts.purchaseCountBefore === 0,

    // Cancelled analytics
    sequenceCancelledExact:
      sessionAnalytics.sequenceCancelledExact[exactKey] || 0,
    sequenceCancelledFeature: purchases.cancelledFeature[opts.feature] || 0,
    sequenceCancelledScreen:
      sessionAnalytics.sequenceCancelledScreen[screenKey] || 0,
    sequenceCancelledSession: sessionAnalytics.sequenceCancelledSession,
    sequenceCancelledToday: getTodayCounter(timeNow, purchases.cancelledToday),

    // Gems analytics
    beforeFreeGems: opts.beforeFreeGems,
    beforePaidGems: opts.beforePaidGems,
    afterFreeGems: opts.afterFreeGems,
    afterPaidGems: opts.afterPaidGems,
  });

  // Send purchase flow end event
  trackPurchaseFlowEnd(opts);

  // Reset cancelled analytics
  StateObserver.invoke.resetCancelledAnalytics({
    featureKey: opts.feature,
    timeNow,
  });
  StateObserver.dispatch(resetCancelledExact());
  StateObserver.dispatch(resetCancelledSession());
}
